import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import Api from 'app/helpers/api';
import Me from 'app/helpers/me';
import { take } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-relatorios-estatisticas-grafico',
  templateUrl: './relatorios-estatisticas-grafico.component.html',
  styleUrls: ['./relatorios-estatisticas-grafico.component.scss']
})
export class RelatoriosEstatisticasGraficoComponent implements OnInit {
  @ViewChild('reportContent') reportContent: ElementRef<HTMLElement>;
  @ViewChild('visaoGeralContent') visaoGeralContent: ElementRef<HTMLElement>;
  @ViewChild('tabContent') tabContent: ElementRef<HTMLElement>;

  apiCall = this.api.new().silent();
  loading = true;
  generatingPdf = false;
  data: any = null;
  activeTab = 'ativos';
  nomeCidade = '';

  constructor(private api: Api, private me: Me, private cdr: ChangeDetectorRef) {}

  async ngOnInit() {
    const dadosUsuario = this.me.currentUserValue;
    const json = JSON.parse(JSON.stringify(dadosUsuario));
    const cidade = json.user.cidade;
    this.nomeCidade = json.user.nomeCidade || '';
    if (!cidade) {
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.data = await this.apiCall
        .get(`servidor/relatorio/estatisticas/completo?id_cidade=${cidade}`)
        .pipe(take(1))
        .toPromise();
    } catch (e) {
      console.error('Erro ao carregar relatório:', e);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  // === Totais ===
  get totais() { return this.data?.totais; }
  get totalServidores() { return this.totais?.total_servidores || 0; }
  get totalActive() { return this.totais?.ativos || 0; }
  get totalInactive() { return this.totais?.inativos || 0; }
  get totalPensioners() { return this.totais?.pensionistas || 0; }
  get totalMaleGeneral() { return this.totais?.distribuicao_sexo?.masculino || 0; }
  get totalFemaleGeneral() { return this.totais?.distribuicao_sexo?.feminino || 0; }

  // === Dados da aba ativa ===
  get currentData() {
    if (!this.data) return null;
    return this.data[this.activeTab] || null;
  }

  get currentCount() { return this.currentData?.total || 0; }
  get currentMale() { return this.currentData?.distribuicao_sexo?.masculino || 0; }
  get currentFemale() { return this.currentData?.distribuicao_sexo?.feminino || 0; }

  get currentAvgSalary(): number {
    const d = this.currentData;
    if (!d) return 0;
    return d.salario_medio ?? d.beneficio_medio ?? 0;
  }

  get currentMaleSalary(): number {
    const d = this.currentData;
    if (!d) return 0;
    return d.salario_medio_masculino ?? d.beneficio_medio_masculino ?? 0;
  }

  get currentFemaleSalary(): number {
    const d = this.currentData;
    if (!d) return 0;
    return d.salario_medio_feminino ?? d.beneficio_medio_feminino ?? 0;
  }

  get currentAvgAge() { return this.currentData?.idade_media || 0; }
  get currentMaleAge() { return this.currentData?.idade_media_masculino || 0; }
  get currentFemaleAge() { return this.currentData?.idade_media_feminino || 0; }

  get currentAgeDistribution(): { label: string; count: number }[] {
    const dist = this.currentData?.distribuicao_idade;
    if (!dist) return [];
    return Object.entries(dist)
      .map(([key, value]) => ({
        label: key.replace('_', '-'),
        count: (value as number) || 0
      }))
      .filter(item => item.count > 0);
  }

  get maxAgeCount(): number {
    const dist = this.currentAgeDistribution;
    if (!dist || dist.length === 0) return 1;
    return Math.max(...dist.map(d => d.count), 1);
  }

  get totalAgeCount(): number {
    const dist = this.currentAgeDistribution;
    if (!dist || dist.length === 0) return 0;
    return dist.reduce((acc, d) => acc + d.count, 0);
  }

  get ageAxisTicks(): number[] {
    const max = this.maxAgeCount;
    if (max <= 1) return [0];
    const step = Math.ceil(max / 6);
    const ticks: number[] = [];
    for (let i = 0; i <= max; i += step) {
      ticks.push(i);
    }
    if (ticks[ticks.length - 1] < max) {
      ticks.push(max);
    }
    return ticks;
  }

  // === Tempo de Serviço (só ativos) ===
  get tempoServicoINSS() { return this.data?.tempo_servico?.inss || null; }
  get tempoServicoRPPS() { return this.data?.tempo_servico?.rpps || null; }

  // === Cônjuges e Dependentes (por aba) ===
  get currentConjugesDependentes() {
    if (!this.data?.conjuges_dependentes) return null;
    return this.data.conjuges_dependentes[this.activeTab] || null;
  }

  // === Labels ===
  get tabLabel(): string {
    switch (this.activeTab) {
      case 'ativos': return 'Ativos';
      case 'inativos': return 'Inativos';
      case 'pensionistas': return 'Pensionistas';
      default: return '';
    }
  }

  get salaryLabel(): string {
    return this.activeTab === 'ativos' ? 'Salário Médio' : 'Valor Médio de Benefício';
  }

  // === Helpers ===
  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  private captureCanvas(el: HTMLElement): Promise<HTMLCanvasElement> {
    return html2canvas(el, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff' });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async baixarPdf() {
    if (this.generatingPdf || !this.visaoGeralContent || !this.tabContent) return;

    const abaOriginal = this.activeTab;
    this.generatingPdf = true;
    this.cdr.detectChanges();

    try {
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Margens (mm)
      const margin = 12;
      const headerHeight = 20; // faixa reservada ao cabecalho no topo de cada pagina
      const contentWidth = pageWidth - margin * 2;
      const contentTop = margin + headerHeight;
      const bottomLimit = pageHeight - margin;

      const dataExportacao = new Date().toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });

      const desenharCabecalho = () => {
        pdf.setTextColor(78, 121, 119); // #4e7977
        pdf.setFontSize(15);
        pdf.setFont('helvetica', 'bold');
        const titulo = this.nomeCidade
          ? `Relatório & Estatísticas - ${this.nomeCidade}`
          : 'Relatório & Estatísticas';
        pdf.text(titulo, margin, margin + 6);

        pdf.setTextColor(110, 110, 110);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Exportado em ${dataExportacao}`, margin, margin + 12);

        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, margin + 15, pageWidth - margin, margin + 15);
      };

      // Cursor de fluxo vertical (mm). Comeca logo abaixo do cabecalho.
      let cursorY = contentTop;
      desenharCabecalho();

      const novaPagina = () => {
        pdf.addPage();
        desenharCabecalho();
        cursorY = contentTop;
      };

      const addSectionTitle = (texto: string) => {
        const alturaTitulo = 12;
        if (cursorY + alturaTitulo > bottomLimit) novaPagina();
        pdf.setTextColor(29, 29, 29); // #1D1D1D
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(texto, margin, cursorY + 7);
        cursorY += alturaTitulo;
      };

      // Insere um canvas fatiando-o entre paginas conforme o espaco disponivel.
      const addCanvasFlowing = (srcCanvas: HTMLCanvasElement) => {
        const pxPorMm = srcCanvas.width / contentWidth;
        let srcY = 0;
        while (srcY < srcCanvas.height) {
          let espacoMm = bottomLimit - cursorY;
          if (espacoMm < 20) {
            novaPagina();
            espacoMm = bottomLimit - cursorY;
          }
          const espacoPx = espacoMm * pxPorMm;
          const fatiaPx = Math.floor(Math.min(espacoPx, srcCanvas.height - srcY));

          const tmp = document.createElement('canvas');
          tmp.width = srcCanvas.width;
          tmp.height = fatiaPx;
          const ctx = tmp.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, tmp.width, tmp.height);
          ctx.drawImage(srcCanvas, 0, srcY, srcCanvas.width, fatiaPx, 0, 0, srcCanvas.width, fatiaPx);

          const fatiaData = tmp.toDataURL('image/jpeg', 0.8);
          const fatiaMm = fatiaPx / pxPorMm;
          pdf.addImage(fatiaData, 'JPEG', margin, cursorY, contentWidth, fatiaMm);
          cursorY += fatiaMm;
          srcY += fatiaPx;

          if (srcY < srcCanvas.height) novaPagina();
        }
        cursorY += 6; // respiro entre secoes
      };

      // === Visão Geral (independente da aba) ===
      addSectionTitle('Visão Geral');
      addCanvasFlowing(await this.captureCanvas(this.visaoGeralContent.nativeElement));

      // === Uma secao por aba ===
      const abas = [
        { key: 'ativos', label: 'Servidores Ativos' },
        { key: 'inativos', label: 'Servidores Inativos (Aposentados)' },
        { key: 'pensionistas', label: 'Servidores Pensionistas' }
      ];

      for (const aba of abas) {
        this.activeTab = aba.key;
        this.cdr.detectChanges();
        await this.delay(350); // deixa as barras/transicoes assentarem antes de capturar

        addSectionTitle(aba.label);
        addCanvasFlowing(await this.captureCanvas(this.tabContent.nativeElement));
      }

      const slug = (this.nomeCidade || 'municipio')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      pdf.save(`relatorio-estatisticas-${slug}.pdf`);
    } catch (e) {
      console.error('Erro ao gerar PDF:', e);
    } finally {
      this.activeTab = abaOriginal;
      this.generatingPdf = false;
      this.cdr.detectChanges();
    }
  }

  formatCurrency(val: number): string {
    if (val == null || isNaN(val) || val === 0) return 'R$ 0,00';
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatNumber(val: number): string {
    if (val == null || isNaN(val)) return '0';
    return val.toLocaleString('pt-BR');
  }

  getPercent(part: number, total: number): number {
    if (!total || total === 0 || !part) return 0;
    return Math.round((part / total) * 100);
  }

  getBarPercent(value: number, max: number): number {
    if (!max || max === 0 || !value) return 0;
    return Math.round((value / max) * 100);
  }

  round(val: number): number {
    return Math.round(isNaN(val) ? 0 : val);
  }
}
